import React, { useEffect, useState } from 'react';
import { NavLink, Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, Mail, Phone, MapPin, Facebook, Instagram, Twitter, AlertTriangle, Vote } from 'lucide-react';
import { CHAPTERS, ASSETS, LATE_REGISTRATION } from '../mock';

const MAIN_NAV = [
  { to: '/', label: 'Home' },
  { to: '/about', label: 'About' },
  { to: '/chapters', label: 'Chapters', hasMenu: true },
  { to: '/conference', label: 'Conference 2026' },
  { to: '/digital-journal', label: 'Digital Ad Journal' },
  { to: '/voter-registration', label: 'Register to Vote' },
  { to: '/news', label: 'News' },
  { to: '/contact', label: 'Contact' },
];

export default function Layout({ children }) {
  const nowMs = Date.now();
  const CONFERENCE_OVER = nowMs >= new Date('2026-11-01T12:00:00-05:00').getTime();
  const VOTE_WINDOW = CONFERENCE_OVER && nowMs < new Date('2026-11-04T00:00:00-05:00').getTime();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [chapterMenuOpen, setChapterMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    if (!location.hash) { window.scrollTo(0, 0); return; }
    const t = setTimeout(() => document.getElementById(location.hash.slice(1))?.scrollIntoView({ behavior: 'smooth' }), 300);
    return () => clearTimeout(t);
  }, [location.pathname, location.hash]);

  return (
    <div className="min-h-screen flex flex-col bg-cbtu-black">
      {VOTE_WINDOW && (
        <Link to="/voter-registration" className="block bg-[#0c1c40] hover:bg-[#13285a] border-b-2 border-gold transition-colors text-white text-center text-xs sm:text-sm px-4 py-2.5 font-heading tracking-wider" data-testid="election-day-banner">
          <Vote size={15} className="inline -mt-0.5 mr-1.5 text-gold" />
          DIDN&apos;T EARLY VOTE? VOTE TUESDAY, NOVEMBER 3! Take a friend and every registered voter in your house to the polls. Get the word out!
        </Link>
      )}
      {LATE_REGISTRATION.active && !CONFERENCE_OVER && (
        <Link to="/conference#register" className="block bg-red-700 hover:bg-red-600 transition-colors text-white text-center text-xs sm:text-sm px-4 py-2 font-heading tracking-wider" data-testid="late-registration-banner">
          <AlertTriangle size={14} className="inline -mt-0.5 mr-1.5" />
          ON-SITE REGISTRATION IS OPEN: register and pay $200 at the conference registration desk. Banquet tickets are still available on-site at registration.
        </Link>
      )}
      {/* Top utility bar */}
      <div className="bg-cbtu-green text-cbtu-cream text-xs">
        <div className="max-w-7xl mx-auto px-4 py-2 flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-4 flex-wrap">
            <span className="flex items-center gap-1.5"><Mail size={12} className="text-gold" /> cbturegioniii@gmail.com</span>
            <span className="hidden sm:flex items-center gap-1.5 text-cbtu-cream/80">Region III Rep: Mike D. Payne (ILA)</span>
          </div>
          <div className="flex items-center gap-3 text-cbtu-cream/80">
            <span className="font-heading tracking-widest text-[10px] text-gold">FLORIDA ★ GEORGIA ★ NORTH CAROLINA ★ SOUTH CAROLINA</span>
          </div>
        </div>
      </div>

      {/* Main header */}
      <header className="sticky top-0 z-40 bg-cbtu-black/95 backdrop-blur border-b border-gold/30">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-3 group">
            <img
              src={ASSETS.roundLogo}
              alt="CBTU Region III"
              className="h-14 w-14 rounded-full object-cover border-2 border-gold flex-shrink-0 shadow-lg bg-cbtu-black"
            />
            <div className="leading-tight">
              <div className="font-display text-2xl gold-text-fill">CBTU</div>
              <div className="font-heading text-xs tracking-[0.25em] text-cbtu-cream">REGION III</div>
            </div>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-7">
            {MAIN_NAV.map((item) => (
              item.hasMenu ? (
                <div
                  key={item.label}
                  className="relative"
                  onMouseEnter={() => setChapterMenuOpen(true)}
                  onMouseLeave={() => setChapterMenuOpen(false)}
                >
                  <NavLink to={item.to} className={({ isActive }) => `nav-link flex items-center gap-1 ${isActive ? 'active' : ''}`}>
                    {item.label} <ChevronDown size={14} />
                  </NavLink>
                  {chapterMenuOpen && (
                    <div className="absolute right-0 top-full pt-3 w-72">
                      <div className="bg-cbtu-navy border border-gold/40 shadow-2xl rounded-sm overflow-hidden">
                        <div className="px-4 py-2 bg-cbtu-green/60 text-gold font-heading text-xs tracking-widest border-b border-gold/30">CHAPTERS</div>
                        <ul>
                          {CHAPTERS.map((c) => (
                            <li key={c.slug}>
                              <Link to={`/chapters/${c.slug}`} className="flex items-center justify-between px-4 py-2.5 text-sm text-cbtu-cream hover:bg-cbtu-green/40 hover:text-gold-bright transition-colors border-b border-gold/10 last:border-b-0">
                                <span>{c.name}</span>
                                <span className="text-[10px] tracking-widest text-gold/70">{c.state.split(' ').map(w => w[0]).join('')}</span>
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <NavLink key={item.to} to={item.to} className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`} end={item.to === '/'}>
                  {item.label}
                </NavLink>
              )
            ))}
          </nav>

          {/* Mobile toggle */}
          <button className="lg:hidden text-gold" onClick={() => setMobileOpen(!mobileOpen)} aria-label="Toggle menu">
            {mobileOpen ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div className="lg:hidden border-t border-gold/30 bg-cbtu-navy">
            <div className="px-4 py-4 flex flex-col gap-1">
              {MAIN_NAV.map((item) => (
                <NavLink key={item.to} to={item.to} onClick={() => setMobileOpen(false)} className={({ isActive }) => `nav-link py-2 ${isActive ? 'active' : ''}`} end={item.to === '/'}>
                  {item.label}
                </NavLink>
              ))}
              <div className="mt-2 pt-3 border-t border-gold/20">
                <div className="text-[11px] text-gold tracking-widest mb-1">CHAPTERS</div>
                {CHAPTERS.map((c) => (
                  <Link key={c.slug} to={`/chapters/${c.slug}`} onClick={() => setMobileOpen(false)} className="block py-1.5 text-sm text-cbtu-cream hover:text-gold-bright">{c.name}</Link>
                ))}
              </div>
            </div>
          </div>
        )}
      </header>

      <main className="flex-1" key={location.pathname}>{children}</main>

      {/* Footer */}
      <footer className="mt-16 bg-cbtu-navy border-t-2 border-gold">
        <div className="green-banner">
          <div className="max-w-7xl mx-auto px-4 py-5 text-center">
            <p className="font-display text-xl md:text-2xl text-cbtu-cream">
              WORKING PEOPLE <span className="gold-text-fill">UNITED</span> CAN <span className="gold-text-fill">NEVER</span> BE <span className="gold-text-fill">DEFEATED</span>
            </p>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-4 py-12 grid md:grid-cols-4 gap-10">
          <div className="md:col-span-1">
            <img
              src={ASSETS.roundLogo}
              alt="CBTU Region III"
              className="h-20 w-20 rounded-full object-cover border-2 border-gold mb-3 shadow-lg bg-cbtu-black"
            />
            <div className="font-display text-2xl gold-text-fill">CBTU REGION III</div>
            <p className="text-sm text-cbtu-muted mt-3 leading-relaxed">Coalition of Black Trade Unionists. Serving Florida, Georgia, North Carolina &amp; South Carolina.</p>
            <div className="flex items-center gap-3 mt-4">
              <a href="#" aria-label="Facebook" className="p-2 border border-gold/40 text-gold hover:bg-gold hover:text-cbtu-black transition-colors rounded-sm"><Facebook size={16} /></a>
              <a href="#" aria-label="Instagram" className="p-2 border border-gold/40 text-gold hover:bg-gold hover:text-cbtu-black transition-colors rounded-sm"><Instagram size={16} /></a>
              <a href="#" aria-label="Twitter" className="p-2 border border-gold/40 text-gold hover:bg-gold hover:text-cbtu-black transition-colors rounded-sm"><Twitter size={16} /></a>
            </div>
          </div>
          <div>
            <div className="font-heading text-sm tracking-widest text-gold mb-3">EXPLORE</div>
            <ul className="space-y-2 text-sm text-cbtu-cream">
              {MAIN_NAV.map(i => <li key={i.to}><Link className="hover:text-gold-bright transition-colors" to={i.to}>{i.label}</Link></li>)}
            </ul>
          </div>
          <div>
            <div className="font-heading text-sm tracking-widest text-gold mb-3">CHAPTERS</div>
            <ul className="space-y-2 text-sm text-cbtu-cream">
              {CHAPTERS.map(c => <li key={c.slug}><Link className="hover:text-gold-bright transition-colors" to={`/chapters/${c.slug}`}>{c.name}</Link></li>)}
            </ul>
          </div>
          <div>
            <div className="font-heading text-sm tracking-widest text-gold mb-3">CONTACT</div>
            <ul className="space-y-2 text-sm text-cbtu-cream">
              <li className="flex items-start gap-2"><MapPin size={14} className="text-gold mt-0.5" /> P.O. Box 8303, Fort Lauderdale, FL 33310</li>
              <li className="flex items-center gap-2"><span className="text-gold">★</span> Region III Rep: Mike D. Payne (ILA)</li>
              <li className="flex items-center gap-2"><Mail size={14} className="text-gold" /> <a href="mailto:cbturegioniii@gmail.com" className="hover:text-gold-bright">cbturegioniii@gmail.com</a></li>
              <li className="flex items-center gap-2"><span className="text-gold">★</span> National: <a href="https://cbtu.org" target="_blank" rel="noreferrer" className="hover:text-gold-bright">cbtu.org</a></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-gold/20">
          <div className="max-w-7xl mx-auto px-4 py-4 flex flex-col md:flex-row items-center justify-between gap-2 text-xs text-cbtu-muted">
            <div>© {new Date().getFullYear()} CBTU Region III. All rights reserved.</div>
            <div className="font-heading tracking-widest text-gold">FOUNDED 1972 ★ CHICAGO, IL</div>
          </div>
        </div>
      </footer>
    </div>
  );
}
